import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import { useToast } from '../../context/ToastContext.jsx';
import LoadingState from '../LoadingState.jsx';
import EmptyState from '../EmptyState.jsx';
import StatusBadge from '../StatusBadge.jsx';

const NEXT_STATUS = { draft: 'running', running: 'completed', completed: 'archived' };
const NEXT_LABEL = { draft: 'Start', running: 'Complete', completed: 'Archive' };

/**
 * A/B test lifecycle management — migrated from packages/dashboard's AbTestsTab.jsx
 * (packages/dashboard/src/components/AbTestsTab.jsx) as part of the dashboard consolidation phase.
 * Reuses the existing admin API unchanged (GET/POST /api/publishers/:id/ab-tests,
 * PATCH /api/ab-tests/:id) in both entry points; no backend or data-model change.
 */
export default function AbTestsTab({ publisherId }) {
  const toast = useToast();
  const [tests, setTests] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', variantA: 'control', variantB: 'variant_b', splitA: 50 });
  const [creating, setCreating] = useState(false);
  const [transitioningId, setTransitioningId] = useState(null);

  async function load() {
    setError(null);
    try {
      setTests(await api.listAbTests(publisherId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load A/B tests.');
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publisherId]);

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    try {
      const splitA = Number(form.splitA);
      await api.createAbTest(publisherId, {
        name: form.name,
        variants: [
          { name: form.variantA, traffic_pct: splitA },
          { name: form.variantB, traffic_pct: 100 - splitA },
        ],
      });
      toast.success('A/B test created as a draft.');
      setForm({ name: '', variantA: 'control', variantB: 'variant_b', splitA: 50 });
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create test.');
    } finally {
      setCreating(false);
    }
  }

  async function advance(test) {
    const next = NEXT_STATUS[test.status];
    if (!next) return;
    setTransitioningId(test.id);
    try {
      const body = { status: next };
      if (next === 'running') body.started_at = new Date().toISOString();
      if (next === 'completed') body.ended_at = new Date().toISOString();
      await api.updateAbTest(test.id, body);
      toast.success(`"${test.name}" is now ${next}.`);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update test.');
    } finally {
      setTransitioningId(null);
    }
  }

  if (error) return <EmptyState title="Couldn't load A/B tests" message={error} />;
  if (tests === null) return <LoadingState label="Loading A/B tests" />;

  return (
    <div className="stack">
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">New A/B test</h2>
          <span className="card__hint">Starts as a draft — no traffic is affected until you start it</span>
        </div>
        <form className="form form--grid" onSubmit={handleCreate}>
          <label className="field">
            <span className="field__label">Test name</span>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="field__label">Split (A / B) %</span>
            <input
              type="number"
              min="0"
              max="100"
              value={form.splitA}
              onChange={(e) => setForm({ ...form, splitA: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="field__label">Variant A name</span>
            <input value={form.variantA} onChange={(e) => setForm({ ...form, variantA: e.target.value })} />
          </label>
          <label className="field">
            <span className="field__label">Variant B name</span>
            <input value={form.variantB} onChange={(e) => setForm({ ...form, variantB: e.target.value })} />
          </label>
          <div className="form__actions form__actions--grid-span">
            <button type="submit" className="btn btn--primary" disabled={creating}>
              {creating ? 'Creating…' : 'Create A/B test'}
            </button>
          </div>
        </form>
      </section>

      <section className="card card--flush">
        <div className="card__header card__header--padded">
          <h2 className="card__title">Tests</h2>
        </div>
        {tests.length === 0 ? (
          <div className="card__padded">
            <EmptyState title="No A/B tests yet" message="Create one above to start comparing configurations." />
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Variants</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tests.map((t) => (
                  <tr key={t.id}>
                    <td className="cell-title">{t.name}</td>
                    <td className="cell-muted">
                      {(t.variants || []).map((v) => `${v.name} (${v.traffic_pct}%)`).join(' vs ')}
                    </td>
                    <td>
                      <StatusBadge status={t.status} />
                    </td>
                    <td>
                      {NEXT_STATUS[t.status] && (
                        <button
                          className="btn btn--ghost btn--sm"
                          disabled={transitioningId === t.id}
                          onClick={() => advance(t)}
                        >
                          {transitioningId === t.id ? 'Working…' : NEXT_LABEL[t.status]}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
